import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { CampaignTypePicker } from "../components/CampaignTypePicker";
import { LivePreview } from "../components/LivePreview";
import { apiFetch } from "../api";
import {
  CAMPAIGN_TYPE_IDS,
  campaignTypeDefinition,
  campaignTypeFromStored,
  type CampaignType,
} from "../../src/domain/campaignTypes";
import {
  CAMPAIGN_RECOMMENDATION_MODES,
  campaignRecommendationsFromStored,
  campaignTargetingFromStored,
  legacyRuleFromTargeting,
  type CampaignAdditionalCondition,
  type CampaignAdditionalConditionType,
  type CampaignRecommendationIntent,
  type CampaignRecommendationMode,
  type CampaignTargeting,
  type CampaignTargetingMode,
} from "../../src/domain/campaignConfig";

type PickedResource = {
  id: string;
  title: string;
  handle?: string;
};

type LocaleText = Record<string, string>;

type PickedVariant = PickedResource & {
  productId?: string;
  productTitle?: string;
  imageUrl?: string;
};

type PickedProduct = PickedResource & {
  productId: string;
  variantId: string;
  variantTitle?: string;
  handle?: string;
  imageUrl?: string;
  bundleLabel?: LocaleText;
  bundleDescription?: LocaleText;
  available?: boolean;
};

type ServiceOffer = {
  id: string;
  title: string;
  description?: string | LocaleText;
  badge?: string | LocaleText;
  variant?: PickedVariant;
  selectedByDefault: boolean;
};

type LocaleOption = {
  locale: string;
  label: string;
};

type ProductPageBlockStatus = {
  themeEditorSeenAt?: string;
  storefrontSeenAt?: string;
};

type CopyFields = {
  modalTitle: string;
  subtitle: string;
  servicesHeading: string;
  serviceBadge: string;
  productsHeading: string;
  discountBadgeLabel: string;
  productAddLabel: string;
  productAddedLabel: string;
  productVariantLabel: string;
  productsPreviousLabel: string;
  productsNextLabel: string;
  closeLabel: string;
  selectedProductError: string;
  selectedItemsError: string;
  addFailedError: string;
  addedToCartLabel: string;
  bundleExpertBadgeLabel: string;
  bundleItemLabel: string;
  bundleItemDescription: string;
  bundleCompletePriceLabel: string;
  bundleSelectedPriceLabel: string;
  bundleCompleteLabel: string;
  bundleSelectedLabel: string;
  bundleAddingLabel: string;
  continueShoppingLabel: string;
  continueLabel: string;
  declineLabel: string;
};

type SavedCampaign = {
  id: string;
  name: string;
  status: "draft" | "active";
  priority: number;
  campaignType?: CampaignType;
  trigger?: "pre_checkout" | "after_add" | "product_page";
  targeting?: CampaignTargeting;
  rule: {
    condition: string;
    value: string;
  };
  service: {
    title: string;
    variant?: PickedResource;
    selectedByDefault: boolean;
  };
  services?: ServiceOffer[];
  recommendations: {
    mode?: CampaignRecommendationMode;
    intent?: CampaignRecommendationIntent;
    products: Array<Partial<PickedProduct> & PickedResource>;
    collections: PickedResource[];
    excludeInCart: boolean;
    excludeUnavailable: boolean;
    discount?: {
      enabled: boolean;
      percentage: number;
      countdownMinutes: number;
    };
    bundle?: {
      preselectAll: boolean;
      multiplyByTriggerQuantity: boolean;
    };
  };
  copy: Record<string, CopyFields>;
  design: {
    layout: string;
    maxRecommendedProducts: number;
    accentColor: string;
    buttonTextColor: string;
    modalBackgroundColor: string;
    textColor: string;
  };
};

type PickerImage = {
  url?: string;
  originalSrc?: string;
  transformedSrc?: string;
  src?: string;
};

type PickerResource = {
  id: string;
  title?: string;
  displayName?: string;
  handle?: string;
  availableForSale?: boolean;
  image?: PickerImage;
  images?: PickerImage[];
  featuredImage?: PickerImage;
  featuredMedia?: { preview?: { image?: PickerImage }; image?: PickerImage };
  product?: {
    id?: string;
    title?: string;
    image?: PickerImage;
    images?: PickerImage[];
    featuredImage?: PickerImage;
    featuredMedia?: { preview?: { image?: PickerImage }; image?: PickerImage };
  };
  variants?: Array<{
    id: string;
    title?: string;
    displayName?: string;
    available?: boolean;
    availableForSale?: boolean;
    image?: PickerImage;
  }>;
};

type ShopifyGlobal = {
  idToken?: () => Promise<string>;
  resourcePicker?: (options: {
    type: "product" | "variant" | "collection";
    action?: "add" | "select";
    multiple?: boolean | number;
    filter?: Record<string, unknown>;
  }) => Promise<PickerResource[] | undefined>;
};

declare global {
  interface Window {
    shopify?: ShopifyGlobal;
  }
}

const defaultCopy: CopyFields = {
  modalTitle: "Complete your cart",
  subtitle: "Add useful extras before checkout. Nothing is required.",
  servicesHeading: "Optional services",
  serviceBadge: "87% choose delivery protection",
  productsHeading: "Recommended products",
  discountBadgeLabel: "{percentage}% off",
  productAddLabel: "Add",
  productAddedLabel: "Added",
  productVariantLabel: "Choose variant",
  productsPreviousLabel: "Previous products",
  productsNextLabel: "Next products",
  closeLabel: "Close",
  selectedProductError: "The selected product could not be added to cart. Choose another product or continue without it.",
  selectedItemsError: "Some selected items could not be added to cart.",
  addFailedError: "Selected items could not be added to cart. Choose another product or continue without it.",
  addedToCartLabel: "Added to cart",
  bundleExpertBadgeLabel: "Recommended by our experts",
  bundleItemLabel: "Recommended addition",
  bundleItemDescription: "Complements your selected product.",
  bundleCompletePriceLabel: "Complete bundle price",
  bundleSelectedPriceLabel: "Selected products price",
  bundleCompleteLabel: "Add complete bundle",
  bundleSelectedLabel: "Add selected products",
  bundleAddingLabel: "Adding products...",
  continueShoppingLabel: "Continue without extras",
  continueLabel: "Continue to checkout",
  declineLabel: "Continue without extras",
};

function newServiceOffer(index: number): ServiceOffer {
  return {
    id: `service-${Date.now()}-${index}`,
    title: index === 0 ? "Package protection" : "Additional service",
    description: { en: index === 0 ? "Protect your delivery against loss or damage." : "" },
    badge: { en: index === 0 ? "87% choose delivery protection" : "" },
    selectedByDefault: false,
  };
}

function imageUrlFrom(...sources: Array<PickerImage | undefined>) {
  for (const source of sources) {
    const url = source?.url ?? source?.originalSrc ?? source?.transformedSrc ?? source?.src;
    if (url) return url;
  }
  return undefined;
}

function productImageFrom(resource: PickerResource) {
  return imageUrlFrom(
    resource.image,
    resource.featuredImage,
    resource.featuredMedia?.preview?.image,
    resource.featuredMedia?.image,
    resource.images?.[0],
    resource.product?.image,
    resource.product?.featuredImage,
    resource.product?.featuredMedia?.preview?.image,
    resource.product?.featuredMedia?.image,
    resource.product?.images?.[0],
  );
}

function normalizeBadge(badge: ServiceOffer["badge"]): LocaleText {
  if (!badge) return {};
  if (typeof badge === "string") return { en: badge };
  return badge;
}

function normalizeDescription(description: ServiceOffer["description"]): LocaleText {
  if (!description) return {};
  if (typeof description === "string") return { en: description };
  return description;
}

function localizedProductValue(value: LocaleText | undefined, locale: string, fallback: string) {
  return value?.[locale] ?? value?.en ?? fallback;
}

function badgeValue(service: ServiceOffer, locale: string) {
  const badge = normalizeBadge(service.badge);
  return badge[locale] ?? badge.en ?? "";
}

function descriptionValue(service: ServiceOffer, locale: string) {
  const description = normalizeDescription(service.description);
  return description[locale] ?? description.en ?? "";
}

export function CampaignEditor() {
  const { id } = useParams();
  const campaignId = id && id !== "new" ? id : undefined;
  const [campaignType, setCampaignType] = useState<CampaignType | null>(null);
  const [campaignName, setCampaignName] = useState("");
  const [campaignStatus, setCampaignStatus] = useState<"draft" | "active">("draft");
  const [trigger, setTrigger] = useState<"pre_checkout" | "after_add" | "product_page">("after_add");
  const [targetingMode, setTargetingMode] = useState<CampaignTargetingMode>("specific_collections");
  const [targetProducts, setTargetProducts] = useState<PickedResource[]>([]);
  const [targetCollections, setTargetCollections] = useState<PickedResource[]>([]);
  const [additionalConditions, setAdditionalConditions] = useState<CampaignAdditionalCondition[]>([]);
  const [priority, setPriority] = useState(10);
  const [services, setServices] = useState<ServiceOffer[]>([newServiceOffer(0)]);
  const [recommendedProducts, setRecommendedProducts] = useState<PickedProduct[]>([]);
  const [collections, setCollections] = useState<PickedResource[]>([]);
  const [recommendationMode, setRecommendationMode] = useState<CampaignRecommendationMode>(
    CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS,
  );
  const [recommendationIntent, setRecommendationIntent] = useState<CampaignRecommendationIntent>("related");
  const [excludeInCart, setExcludeInCart] = useState(true);
  const [excludeUnavailable, setExcludeUnavailable] = useState(true);
  const [preselectAll, setPreselectAll] = useState(true);
  const [multiplyByTriggerQuantity, setMultiplyByTriggerQuantity] = useState(true);
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(10);
  const [discountCountdownMinutes, setDiscountCountdownMinutes] = useState(10);
  const [layout, setLayout] = useState("services_first");
  const [maxRecommendedProducts, setMaxRecommendedProducts] = useState(4);
  const [accentColor, setAccentColor] = useState("#126249");
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff");
  const [modalBackgroundColor, setModalBackgroundColor] = useState("#ffffff");
  const [textColor, setTextColor] = useState("#202223");
  const [locales, setLocales] = useState<LocaleOption[]>([{ locale: "en", label: "English" }]);
  const [activeLocale, setActiveLocale] = useState("en");
  const [copy, setCopy] = useState<Record<string, CopyFields>>({ en: defaultCopy });
  const [pickerError, setPickerError] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [discountSetupUrl, setDiscountSetupUrl] = useState("");
  const [productPageBlockUrl, setProductPageBlockUrl] = useState("");
  const [productPageBlockStatus, setProductPageBlockStatus] = useState<ProductPageBlockStatus>({});
  const [saving, setSaving] = useState(false);
  const [loadingCampaign, setLoadingCampaign] = useState(Boolean(campaignId));

  useEffect(() => {
    apiFetch("/api/locales")
      .then((response) => (response.ok ? response.json() : undefined))
      .then((payload: { locales?: LocaleOption[] } | undefined) => {
        if (payload?.locales?.length) {
          setLocales(payload.locales);
          setActiveLocale(payload.locales[0].locale);
          setCopy((current) => {
            const next = { ...current };
            for (const locale of payload.locales ?? []) {
              next[locale.locale] = next[locale.locale] ?? defaultCopy;
            }
            return next;
          });
        }
      })
      .catch(() => {});
  }, []);

  async function loadProductPageBlockStatus() {
    return apiFetch("/api/onboarding")
      .then((response) => (response.ok ? response.json() : undefined))
      .then((payload: { productPageBlockUrl?: string; productPageBlockStatus?: ProductPageBlockStatus } | undefined) => {
        setProductPageBlockUrl(payload?.productPageBlockUrl ?? "");
        setProductPageBlockStatus(payload?.productPageBlockStatus ?? {});
      })
      .catch(() => {});
  }

  useEffect(() => {
    void loadProductPageBlockStatus();
    const interval = window.setInterval(() => void loadProductPageBlockStatus(), 5_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!campaignId) return;

    apiFetch("/api/campaigns")
      .then((response) => (response.ok ? response.json() : undefined))
      .then((payload: { campaigns?: SavedCampaign[] } | undefined) => {
        const campaign = payload?.campaigns?.find((item) => item.id === campaignId);
        if (!campaign) {
          setSaveStatus("Campaign could not be loaded.");
          return;
        }

        const storedCampaignType = campaignTypeFromStored(campaign);
        setCampaignType(storedCampaignType);
        setCampaignName(campaign.name);
        setCampaignStatus(campaign.status);
        setTrigger(campaignTypeDefinition(storedCampaignType).trigger);
        const targeting = campaignTargetingFromStored(campaign);
        setTargetingMode(targeting.mode);
        setTargetProducts(targeting.products);
        setTargetCollections(targeting.collections);
        setAdditionalConditions(targeting.additionalConditions);
        setPriority(campaign.priority);
        setServices(
          campaign.services?.length
            ? campaign.services
            : [
                {
                  id: "legacy-service",
                  title: campaign.service.title || "Package protection",
                  badge: { en: campaign.copy.en?.serviceBadge ?? defaultCopy.serviceBadge },
                  variant: campaign.service.variant,
                  selectedByDefault: campaign.service.selectedByDefault,
                },
              ],
        );
        const products = campaign.recommendations.products.flatMap((product) => {
          if (!product.variantId) return [];
          return {
            id: product.id,
            productId: product.productId ?? product.id,
            variantId: product.variantId,
            title: product.title,
            variantTitle: product.variantTitle,
            handle: product.handle,
            imageUrl: product.imageUrl,
            bundleLabel: typeof product.bundleLabel === "string" ? { en: product.bundleLabel } : product.bundleLabel,
            bundleDescription: typeof product.bundleDescription === "string" ? { en: product.bundleDescription } : product.bundleDescription,
            available: product.available,
          };
        });
        setRecommendedProducts(products);
        if (campaign.recommendations.products.length > products.length) {
          setPickerError("Some saved cross-sell products need to be picked again before they can be added to cart.");
        }
        const recommendationSettings = campaignRecommendationsFromStored(campaign);
        setRecommendationMode(recommendationSettings.mode);
        setRecommendationIntent(recommendationSettings.intent);
        setCollections(recommendationSettings.collections);
        setExcludeInCart(campaign.recommendations.excludeInCart);
        setExcludeUnavailable(campaign.recommendations.excludeUnavailable);
        setPreselectAll(campaign.recommendations.bundle?.preselectAll ?? false);
        setMultiplyByTriggerQuantity(campaign.recommendations.bundle?.multiplyByTriggerQuantity ?? false);
        setDiscountEnabled(Boolean(campaign.recommendations.discount?.enabled));
        setDiscountPercentage(campaign.recommendations.discount?.percentage ?? 10);
        setDiscountCountdownMinutes(campaign.recommendations.discount?.countdownMinutes ?? 10);
        setCopy(
          Object.fromEntries(
            Object.entries(campaign.copy).map(([locale, fields]) => [
              locale,
              {
                ...defaultCopy,
                ...fields,
              },
            ]),
          ),
        );
        setLayout(campaign.design.layout);
        setMaxRecommendedProducts(campaign.design.maxRecommendedProducts);
        setAccentColor(campaign.design.accentColor);
        setButtonTextColor(campaign.design.buttonTextColor);
        setModalBackgroundColor(campaign.design.modalBackgroundColor);
        setTextColor(campaign.design.textColor);
      })
      .catch(() => setSaveStatus("Campaign could not be loaded."))
      .finally(() => setLoadingCampaign(false));
  }, [campaignId]);

  const activeCopy = useMemo(() => copy[activeLocale] ?? defaultCopy, [activeLocale, copy]);
  const selectedCampaignType = campaignType ? campaignTypeDefinition(campaignType) : null;
  const isAfterAddProducts = campaignType === CAMPAIGN_TYPE_IDS.AFTER_ADD_PRODUCTS;
  const isProductPageAddons = campaignType === CAMPAIGN_TYPE_IDS.PRODUCT_PAGE_ADDONS;
  const isPreCheckout = campaignType === CAMPAIGN_TYPE_IDS.PRE_CHECKOUT_UPSELL;
  const isProductOnly = isAfterAddProducts || isProductPageAddons;
  const productPageBlockLive = Boolean(productPageBlockStatus.storefrontSeenAt);
  const productPageBlockDetected = productPageBlockLive || Boolean(productPageBlockStatus.themeEditorSeenAt);

  function selectCampaignType(nextCampaignType: CampaignType) {
    const definition = campaignTypeDefinition(nextCampaignType);
    setCampaignType(nextCampaignType);
    setTrigger(definition.trigger);
    setCampaignName(definition.defaultName);
    setCampaignStatus("draft");
    setTargetingMode(definition.defaultCondition === "collection" ? "specific_collections" : "specific_products");
    setTargetProducts([]);
    setTargetCollections([]);
    setAdditionalConditions([]);
    setRecommendationMode(CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS);
    setRecommendationIntent("related");
    setRecommendedProducts([]);
    setCollections([]);
    setLayout(definition.defaultLayout);
    if (nextCampaignType === CAMPAIGN_TYPE_IDS.PRODUCT_PAGE_ADDONS) {
      setCopy((current) =>
        Object.fromEntries(
          Object.entries(current).map(([locale, fields]) => [
            locale,
            {
              ...fields,
              modalTitle: "Complete your purchase",
              subtitle: "Add useful extras while you shop.",
              bundleExpertBadgeLabel: "Frequently bought together",
            },
          ]),
        ),
      );
      setMultiplyByTriggerQuantity(false);
    }
    setSaveStatus("");
    setPickerError("");
  }

  function updateCopyField(field: keyof CopyFields, value: string) {
    setCopy((current) => ({
      ...current,
      [activeLocale]: {
        ...(current[activeLocale] ?? defaultCopy),
        [field]: value,
      },
    }));
  }

  function updateProductCopy(id: string, field: "bundleLabel" | "bundleDescription", value: string) {
    setRecommendedProducts((current) =>
      current.map((product) =>
        product.id === id
          ? {
              ...product,
              [field]: {
                ...(product[field] ?? {}),
                [activeLocale]: value,
              },
            }
          : product,
      ),
    );
  }

  function updateService(id: string, updates: Partial<ServiceOffer>) {
    setServices((current) => current.map((service) => (service.id === id ? { ...service, ...updates } : service)));
  }

  function updateServiceBadge(id: string, value: string) {
    setServices((current) =>
      current.map((service) =>
        service.id === id
          ? {
              ...service,
              badge: {
                ...normalizeBadge(service.badge),
                [activeLocale]: value,
              },
            }
          : service,
      ),
    );
  }

  function updateServiceDescription(id: string, value: string) {
    setServices((current) =>
      current.map((service) =>
        service.id === id
          ? {
              ...service,
              description: {
                ...normalizeDescription(service.description),
                [activeLocale]: value,
              },
            }
          : service,
      ),
    );
  }

  function addService() {
    setServices((current) => [...current, newServiceOffer(current.length)]);
  }

  function removeService(id: string) {
    setServices((current) => (current.length > 1 ? current.filter((service) => service.id !== id) : current));
  }

  async function pickVariant(serviceId: string) {
    setPickerError("");
    if (!window.shopify?.resourcePicker) {
      setPickerError("Open this page inside Shopify admin to use the product picker.");
      return;
    }

    const selected = await window.shopify.resourcePicker({
      type: "variant",
      action: "select",
      multiple: false,
    });

    const variant = selected?.[0];
    if (variant) {
      updateService(serviceId, {
        variant: {
          id: variant.id,
          title: variant.displayName ?? variant.title ?? "Selected variant",
          productId: variant.product?.id,
          productTitle: variant.product?.title,
          imageUrl: imageUrlFrom(variant.image) ?? productImageFrom(variant),
        },
      });
    }
  }

  async function pickProducts() {
    setPickerError("");
    if (!window.shopify?.resourcePicker) {
      setPickerError("Open this page inside Shopify admin to use the product picker.");
      return;
    }

    const selected = await window.shopify.resourcePicker({
      type: "product",
      action: "add",
      multiple: 8,
      filter: {
        hidden: false,
        variants: true,
      },
    });

    if (selected) {
      const products = selected.flatMap((product) => {
        const variant = product.variants?.find((item) => item.available !== false && item.availableForSale !== false);
        if (!variant?.id) return [];
        return {
          id: variant.id,
          productId: product.id,
          variantId: variant.id,
          title: product.title ?? "Selected product",
          variantTitle: variant.displayName ?? variant.title,
          handle: product.handle,
          imageUrl: imageUrlFrom(variant.image) ?? productImageFrom(product),
          bundleLabel: recommendedProducts.find((saved) => saved.productId === product.id)?.bundleLabel ?? {},
          bundleDescription: recommendedProducts.find((saved) => saved.productId === product.id)?.bundleDescription ?? {},
          available: true,
        };
      });
      setRecommendedProducts(products);
      if (products.length < selected.length) setPickerError("Some selected products were skipped because they do not have an available variant.");
      if (!products.length) setPickerError("Selected products do not have variants that can be added to cart.");
    }
  }

  async function pickTargetProducts() {
    setPickerError("");
    if (!window.shopify?.resourcePicker) {
      setPickerError("Open this page inside Shopify admin to use the product picker.");
      return;
    }

    const selected = await window.shopify.resourcePicker({
      type: "product",
      action: "select",
      multiple: 50,
      filter: {
        hidden: false,
        variants: false,
      },
    });

    if (selected) setTargetProducts(selected.map((product) => ({ id: product.id, title: product.title ?? "Selected product" })));
  }

  async function pickTargetCollections() {
    setPickerError("");
    if (!window.shopify?.resourcePicker) {
      setPickerError("Open this page inside Shopify admin to use the collection picker.");
      return;
    }

    const selected = await window.shopify.resourcePicker({
      type: "collection",
      action: "select",
      multiple: 50,
    });

    if (selected) setTargetCollections(selected.map((collection) => ({ id: collection.id, title: collection.title ?? "Selected collection" })));
  }

  function addAdditionalCondition() {
    setAdditionalConditions((current) => [
      ...current,
      { id: `condition-${Date.now()}-${current.length}`, condition: "tag", value: "" },
    ]);
  }

  function updateAdditionalCondition(id: string, updates: Partial<CampaignAdditionalCondition>) {
    setAdditionalConditions((current) => current.map((condition) => (condition.id === id ? { ...condition, ...updates } : condition)));
  }

  function removeAdditionalCondition(id: string) {
    setAdditionalConditions((current) => current.filter((condition) => condition.id !== id));
  }

  function removeRecommendedProduct(id: string) {
    setRecommendedProducts((current) => current.filter((product) => product.id !== id));
  }

  function moveRecommendedProduct(id: string, direction: -1 | 1) {
    setRecommendedProducts((current) => {
      const index = current.findIndex((product) => product.id === id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  }

  async function pickCollections() {
    setPickerError("");
    if (!window.shopify?.resourcePicker) {
      setPickerError("Open this page inside Shopify admin to use the collection picker.");
      return;
    }

    const selected = await window.shopify.resourcePicker({
      type: "collection",
      action: "add",
      multiple: 5,
    });

    if (selected) {
      setCollections(
        selected.map((collection) => ({
          id: collection.id,
          title: collection.title ?? "Selected collection",
          handle: collection.handle,
        })),
      );
    }
  }

  async function saveCampaign(nextStatus: "draft" | "active") {
    if (!campaignType) return;
    setSaving(true);
    setSaveStatus("");
    setDiscountSetupUrl("");

    try {
      const targeting: CampaignTargeting = {
        mode: targetingMode,
        products: targetProducts,
        collections: targetCollections,
        additionalConditions: additionalConditions.filter((condition) => condition.value.trim()),
      };
      if (nextStatus === "active" && targetingMode === "specific_products" && targetProducts.length === 0) {
        setSaveStatus("Choose at least one triggering product before activation.");
        return;
      }
      if (nextStatus === "active" && targetingMode === "specific_collections" && targetCollections.length === 0) {
        setSaveStatus("Choose at least one triggering collection before activation.");
        return;
      }
      const validServices = isProductOnly
        ? []
        : services
            .filter((service) => service.title.trim() && service.variant)
            .map((service) => ({
              ...service,
              description: normalizeDescription(service.description),
              badge: normalizeBadge(service.badge),
            }));
      const validProducts = recommendedProducts.map((product) => ({
        ...product,
        bundleLabel: product.bundleLabel ?? {},
        bundleDescription: product.bundleDescription ?? {},
      }));
      if (
        nextStatus === "active" &&
        isProductOnly &&
        recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS &&
        recommendedProducts.length === 0
      ) {
        setSaveStatus(isProductPageAddons ? "Choose at least one product-page add-on." : "Choose at least one bundle product.");
        return;
      }
      if (
        nextStatus === "active" &&
        recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.COLLECTION_PRODUCTS &&
        collections.length === 0
      ) {
        setSaveStatus("Choose at least one recommendation collection.");
        return;
      }
      if (
        nextStatus === "active" &&
        recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.COLLECTION_PRODUCTS &&
        collections.some((collection) => !collection.handle)
      ) {
        setSaveStatus("Pick the recommendation collections again so Shopify can provide their storefront handles.");
        return;
      }
      if (
        nextStatus === "active" &&
        isPreCheckout &&
        !validServices.length &&
        recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS &&
        recommendedProducts.length === 0
      ) {
        setSaveStatus("Choose at least one service variant or cross-sell product.");
        return;
      }

      const response = await apiFetch("/api/campaigns", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: campaignId,
          name: campaignName,
          status: nextStatus,
          priority,
          campaignType,
          trigger,
          targeting,
          rule: legacyRuleFromTargeting(targeting),
          service: {
            title: validServices[0]?.title ?? "",
            variant: validServices[0]?.variant,
            selectedByDefault: validServices[0]?.selectedByDefault ?? false,
          },
          services: validServices,
          recommendations: {
            mode: recommendationMode,
            intent: recommendationIntent,
            products: validProducts,
            collections,
            excludeInCart,
            excludeUnavailable,
            discount: {
              enabled: discountEnabled,
              percentage: Math.min(50, Math.max(0, discountPercentage)),
              countdownMinutes: Math.max(1, discountCountdownMinutes),
            },
            bundle: {
              preselectAll,
              multiplyByTriggerQuantity,
            },
          },
          copy,
          design: {
            layout: isProductOnly ? "products_first" : layout,
            maxRecommendedProducts,
            accentColor,
            buttonTextColor,
            modalBackgroundColor,
            textColor,
          },
        }),
      });

      if (!response.ok) throw new Error("Unable to save campaign");
      const payload = (await response.json()) as {
        discountSetup?: {
          ok?: boolean;
          needsAuth?: boolean;
          setupUrl?: string;
          reason?: string;
        };
      };
      setCampaignStatus(nextStatus);
      if (payload.discountSetup && !payload.discountSetup.ok) {
        setDiscountSetupUrl(payload.discountSetup.setupUrl ?? "");
        setSaveStatus(payload.discountSetup.needsAuth ? "Campaign saved. Confirm Shopify discount permission to activate discounts." : "Campaign saved, but discount activation needs attention.");
      } else {
        setSaveStatus(nextStatus === "active" ? "Campaign activated." : "Draft saved.");
      }
    } catch {
      setSaveStatus("Campaign could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  if (loadingCampaign) {
    return (
      <s-page heading="Campaign editor">
        <s-section heading="Loading campaign" />
      </s-page>
    );
  }

  if (!campaignType) {
    return (
      <s-page heading="Create campaign">
        {saveStatus ? <p className="inline-error">{saveStatus}</p> : null}
        {!campaignId ? <CampaignTypePicker onSelect={selectCampaignType} /> : null}
      </s-page>
    );
  }

  return (
    <s-page heading={campaignId ? "Edit campaign" : "Create campaign"}>
      <header className="page-header">
        <div>
          <span className="step-label">{campaignId ? "Campaign type" : "Step 2 of 2"}</span>
          <s-paragraph>{selectedCampaignType?.editorDescription}</s-paragraph>
          {!campaignId ? (
            <button className="change-type-button" type="button" onClick={() => setCampaignType(null)}>
              ← Change campaign type
            </button>
          ) : null}
        </div>
        <div className="editor-save-actions">
          <button className="polaris-button" type="button" onClick={() => saveCampaign("draft")} disabled={saving}>
            Save draft
          </button>
          <button className="primary" type="button" onClick={() => saveCampaign("active")} disabled={saving}>
            {saving ? "Saving" : campaignStatus === "active" ? "Save active campaign" : "Activate campaign"}
          </button>
        </div>
      </header>
      {saveStatus ? <p className={saveStatus.includes("could not") ? "inline-error" : "inline-success"}>{saveStatus}</p> : null}
      {discountSetupUrl ? (
        <p className="inline-warning">
          <a className="polaris-button" href={discountSetupUrl} target="_top">
            Activate Shopify discounts
          </a>
        </p>
      ) : null}

      <div className="campaign-editor-layout">
        <main className="editor-main">
          <s-section heading="Campaign details">
            <div className="selected-campaign-type">
              <span>Campaign type</span>
              <strong>{selectedCampaignType?.title}</strong>
              <small>{selectedCampaignType?.description}</small>
            </div>
            <div className="form-grid">
              <label>
                Campaign name
                <input value={campaignName} onChange={(event) => setCampaignName(event.currentTarget.value)} />
              </label>
              <label>
                Priority
                <input type="number" value={priority} onChange={(event) => setPriority(Number(event.currentTarget.value))} />
              </label>
            </div>
            <div className="campaign-lifecycle-row">
              <span className={`status-pill ${campaignStatus}`}>{campaignStatus}</span>
              <small>Save an incomplete setup as a draft, then activate it when targeting and products are ready.</small>
            </div>
          </s-section>

          <s-section heading="Placement">
            <div className="placement-summary-card">
              <span className="campaign-type-icon" aria-hidden="true">{selectedCampaignType?.supportsServices ? "▤" : "+"}</span>
              <span>
                <strong>{selectedCampaignType?.placementLabel}</strong>
                <small>{selectedCampaignType?.presentationLabel}</small>
              </span>
              <span className="locked-setting">Set by campaign type</span>
            </div>
            {isProductPageAddons ? (
              <div className="strategy-config-card strategy-summary-card extension-status-card" data-status={productPageBlockLive ? "live" : productPageBlockDetected ? "detected" : "missing"}>
                <span className="strategy-icon" aria-hidden="true">{productPageBlockDetected ? "✓" : "↗"}</span>
                <span>
                  <strong>{productPageBlockLive ? "Product-page block is live" : productPageBlockDetected ? "Product-page block detected" : "Add the block to the product template"}</strong>
                  <small>{productPageBlockLive
                    ? "The block was loaded on your storefront and is ready to show active campaigns."
                    : productPageBlockDetected
                      ? "The block was found in the theme editor. Save the theme and open a product page to confirm it live."
                      : "Place the Product page add-ons app block in the main product section and save the theme."}</small>
                </span>
                <div className="extension-status-actions">
                  <span className={`status-pill ${productPageBlockDetected ? "active" : "draft"}`}>
                    {productPageBlockLive ? "Live" : productPageBlockDetected ? "Detected" : "Not detected"}
                  </span>
                  {productPageBlockUrl && !productPageBlockDetected ? (
                    <a className="polaris-button" href={productPageBlockUrl} rel="noreferrer" target="_blank">
                      Add to theme
                    </a>
                  ) : null}
                  <button className="plain-button" type="button" onClick={() => void loadProductPageBlockStatus()}>Check status</button>
                </div>
              </div>
            ) : null}
          </s-section>

          <s-section heading="Show for">
            <p className="section-description">
              {isAfterAddProducts
                ? "Choose which newly added products can trigger this bundle."
                : isProductPageAddons
                  ? "Choose which product pages display this inline add-ons block."
                  : "Choose which products in the cart can trigger this pre-checkout offer."}
            </p>
            <div className="choice-card-list" role="radiogroup" aria-label="Campaign targeting">
              {([
                ["all", "All products", "Run for every matching cart or newly added product."],
                ["specific_products", "Specific products", "Run only for products you select."],
                ["specific_collections", "Specific collections", "Run for products from selected Shopify collections."],
              ] as Array<[CampaignTargetingMode, string, string]>).map(([mode, title, description]) => (
                <label className="choice-card" data-selected={targetingMode === mode} key={mode}>
                  <input type="radio" name="targeting-mode" checked={targetingMode === mode} onChange={() => setTargetingMode(mode)} />
                  <span><strong>{title}</strong><small>{description}</small></span>
                </label>
              ))}
            </div>
            {targetingMode === "specific_products" ? (
              <div className="targeting-resource-box">
                <button className="polaris-button" type="button" onClick={pickTargetProducts}>Choose products</button>
                <ResourceList resources={targetProducts} onRemove={(id) => setTargetProducts((current) => current.filter((resource) => resource.id !== id))} />
              </div>
            ) : null}
            {targetingMode === "specific_collections" ? (
              <div className="targeting-resource-box">
                <button className="polaris-button" type="button" onClick={pickTargetCollections}>Choose collections</button>
                <ResourceList resources={targetCollections} onRemove={(id) => setTargetCollections((current) => current.filter((resource) => resource.id !== id))} />
              </div>
            ) : null}
            <div className="additional-conditions-header">
              <span><strong>Additional conditions</strong><small>All added conditions must match.</small></span>
              <button className="polaris-button" type="button" onClick={addAdditionalCondition}>Add condition</button>
            </div>
            {additionalConditions.length ? (
              <div className="additional-condition-list">
                {additionalConditions.map((item) => (
                  <div className="additional-condition-row" key={item.id}>
                    <select value={item.condition} onChange={(event) => updateAdditionalCondition(item.id, { condition: event.currentTarget.value as CampaignAdditionalConditionType })}>
                      <option value="tag">Product tag equals</option>
                      <option value="vendor">Vendor equals</option>
                      <option value="product_type">Product type equals</option>
                      {isPreCheckout ? <option value="subtotal_gt">Cart subtotal greater than</option> : null}
                      {isPreCheckout ? <option value="subtotal_lt">Cart subtotal less than</option> : null}
                    </select>
                    <input
                      aria-label="Condition value"
                      type={item.condition.startsWith("subtotal_") ? "number" : "text"}
                      placeholder={item.condition.startsWith("subtotal_") ? "50" : "Value"}
                      value={item.value}
                      onChange={(event) => updateAdditionalCondition(item.id, { value: event.currentTarget.value })}
                    />
                    <button className="plain-button" type="button" onClick={() => removeAdditionalCondition(item.id)}>Remove</button>
                  </div>
                ))}
              </div>
            ) : null}
          </s-section>

          {selectedCampaignType?.supportsServices ? (
          <s-section heading="Services">
            <div className="language-row service-language-row">
              <label>
                Service text language
                <select value={activeLocale} onChange={(event) => setActiveLocale(event.currentTarget.value)}>
                  {locales.map((locale) => (
                    <option key={locale.locale} value={locale.locale}>
                      {locale.label} ({locale.locale})
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="quiet-stack">
              {services.map((service, index) => (
                <div className="service-item" key={service.id}>
                  <div className="service-item-header">
                    <strong>Service {index + 1}</strong>
                    {services.length > 1 ? (
                      <button className="plain-button" type="button" onClick={() => removeService(service.id)}>
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <div className="form-grid">
                    <label>
                      Name shown in popup
                      <input value={service.title} onChange={(event) => updateService(service.id, { title: event.currentTarget.value })} />
                    </label>
                    <label>
                      Badge text ({activeLocale})
                      <input
                        placeholder="87% choose this"
                        value={badgeValue(service, activeLocale)}
                        onChange={(event) => updateServiceBadge(service.id, event.currentTarget.value)}
                      />
                    </label>
                    <label>
                      Short description ({activeLocale})
                      <input
                        placeholder="Protect your delivery against loss or damage."
                        value={descriptionValue(service, activeLocale)}
                        onChange={(event) => updateServiceDescription(service.id, event.currentTarget.value)}
                      />
                    </label>
                    <label>
                      Shopify variant
                      <button className="polaris-button" type="button" onClick={() => pickVariant(service.id)}>
                        {service.variant ? "Change variant" : "Pick variant"}
                      </button>
                    </label>
                  </div>
                  {service.variant ? (
                    <div className="selected-list compact" aria-label="Selected service variant">
                      <span className="selected-resource">
                        {service.variant.imageUrl ? <img alt="" src={service.variant.imageUrl} /> : null}
                        {service.variant.title}
                      </span>
                    </div>
                  ) : null}
                  <label className="setting-row setting-row-standalone compact-row">
                    <span>
                      <strong>Selected by default</strong>
                      <small>Preselect this service in the popup.</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={service.selectedByDefault}
                      onChange={(event) => updateService(service.id, { selectedByDefault: event.currentTarget.checked })}
                    />
                  </label>
                </div>
              ))}
            </div>
            <button className="secondary-button" type="button" onClick={addService}>
              Add service
            </button>
          </s-section>
          ) : null}

          <s-section heading="Recommendation type">
            <p className="section-description">Choose how products are selected for this offer.</p>
            <div className="recommendation-mode-grid" role="radiogroup" aria-label="Recommendation strategy">
              {[
                {
                  mode: CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS,
                  title: "Manual products",
                  description: "Select and order the exact Shopify products shown to customers.",
                  badge: "Curated",
                },
                {
                  mode: CAMPAIGN_RECOMMENDATION_MODES.SHOPIFY_RECOMMENDATIONS,
                  title: "Shopify smart recommendations",
                  description: "Use Shopify product recommendations for the triggering product.",
                  badge: "Automatic",
                },
                {
                  mode: CAMPAIGN_RECOMMENDATION_MODES.COLLECTION_PRODUCTS,
                  title: "Products from collections",
                  description: "Fill the offer dynamically from one or more selected collections.",
                  badge: "Dynamic",
                },
                {
                  mode: CAMPAIGN_RECOMMENDATION_MODES.SAME_PRODUCT,
                  title: "Same product",
                  description: "Offer another variant or quantity of the product that triggered the campaign.",
                  badge: "Dynamic",
                },
              ].map((choice) => (
                <label className="choice-card recommendation-mode-card" data-selected={recommendationMode === choice.mode} key={choice.mode}>
                  <input
                    type="radio"
                    name="recommendation-mode"
                    checked={recommendationMode === choice.mode}
                    onChange={() => setRecommendationMode(choice.mode)}
                  />
                  <span>
                    <strong>{choice.title}</strong>
                    <small>{choice.description}</small>
                  </span>
                  <span className="recommendation-mode-badge">{choice.badge}</span>
                </label>
              ))}
            </div>
            <div className="recommendation-section-header">
              <span>
                <strong>{recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS
                  ? isAfterAddProducts ? "Bundle products" : isProductPageAddons ? "Add-on products" : "Upsell products"
                  : "Strategy settings"}</strong>
                <small>{recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS
                  ? "Products are shown in the order below."
                  : "Products are resolved when the offer opens on the storefront."}</small>
              </span>
              <label>
                Maximum shown
                <select value={maxRecommendedProducts} onChange={(event) => setMaxRecommendedProducts(Number(event.currentTarget.value))}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((count) => <option key={count} value={count}>{count}</option>)}
                </select>
              </label>
            </div>
            {recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS ? (
              <>
                <div className="toolbar">
                  <button className="polaris-button" type="button" onClick={pickProducts}>Pick products</button>
                </div>
                {recommendedProducts.length ? (
                  <div className="offer-product-list" aria-label="Selected recommendation products">
                    {recommendedProducts.map((product, index) => (
                      <article className="offer-product-row" key={product.id}>
                        <span className="offer-product-order">{index + 1}</span>
                        {product.imageUrl ? <img alt="" src={product.imageUrl} /> : <span className="offer-product-placeholder" aria-hidden="true" />}
                        <span className="offer-product-title">
                          <strong>{product.title}</strong>
                          <small>{product.variantTitle || "Default variant"}</small>
                        </span>
                        <span className="offer-product-actions">
                          <button aria-label={`Move ${product.title} up`} disabled={index === 0} type="button" onClick={() => moveRecommendedProduct(product.id, -1)}>↑</button>
                          <button aria-label={`Move ${product.title} down`} disabled={index === recommendedProducts.length - 1} type="button" onClick={() => moveRecommendedProduct(product.id, 1)}>↓</button>
                          <button className="plain-button" type="button" onClick={() => removeRecommendedProduct(product.id)}>Remove</button>
                        </span>
                      </article>
                    ))}
                  </div>
                ) : <div className="recommendation-empty"><strong>No products selected</strong><small>Pick at least one product before activating this campaign.</small></div>}
              </>
            ) : null}
            {recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.SHOPIFY_RECOMMENDATIONS ? (
              <div className="strategy-config-card">
                <label>
                  Recommendation intent
                  <select value={recommendationIntent} onChange={(event) => setRecommendationIntent(event.currentTarget.value as CampaignRecommendationIntent)}>
                    <option value="related">Related products</option>
                    <option value="complementary">Complementary products</option>
                  </select>
                </label>
                <p>Shopify resolves products from the trigger when the offer loads. If complementary products are unavailable, the storefront automatically falls back to related products.</p>
              </div>
            ) : null}
            {recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.COLLECTION_PRODUCTS ? (
              <div className="strategy-config-card">
                <div className="strategy-config-header">
                  <span><strong>Source collections</strong><small>Products are merged, deduplicated and limited by the maximum shown setting.</small></span>
                  <button className="polaris-button" type="button" onClick={pickCollections}>Choose collections</button>
                </div>
                <ResourceList resources={collections} onRemove={(resourceId) => setCollections((current) => current.filter((item) => item.id !== resourceId))} />
              </div>
            ) : null}
            {recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.SAME_PRODUCT ? (
              <div className="strategy-config-card strategy-summary-card">
                <span className="strategy-icon" aria-hidden="true">↻</span>
                <span><strong>Uses the triggering product</strong><small>An available alternative variant is preferred. If none exists, customers can add another quantity of the current variant.</small></span>
              </div>
            ) : null}
            {pickerError ? <p className="inline-error">{pickerError}</p> : null}
            {isProductOnly && recommendationMode === CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS && recommendedProducts.length ? (
              <div className="bundle-product-copy-editor">
                <div className="language-row">
                  <label>
                    Product text language
                    <select value={activeLocale} onChange={(event) => setActiveLocale(event.currentTarget.value)}>
                      {locales.map((locale) => (
                        <option key={locale.locale} value={locale.locale}>
                          {locale.label} ({locale.locale})
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="quiet-stack">
                  {recommendedProducts.map((product) => (
                    <div className="bundle-product-copy-item" key={`copy-${product.id}`}>
                      <div className="bundle-product-copy-title">
                        {product.imageUrl ? <img alt="" src={product.imageUrl} /> : null}
                        <strong>{product.title}</strong>
                      </div>
                      <div className="form-grid">
                        <label>
                          Need / category ({activeLocale})
                          <input
                            value={localizedProductValue(product.bundleLabel, activeLocale, activeCopy.bundleItemLabel)}
                            onChange={(event) => updateProductCopy(product.id, "bundleLabel", event.currentTarget.value)}
                          />
                        </label>
                        <label>
                          Benefit text ({activeLocale})
                          <input
                            value={localizedProductValue(product.bundleDescription, activeLocale, activeCopy.bundleItemDescription)}
                            onChange={(event) => updateProductCopy(product.id, "bundleDescription", event.currentTarget.value)}
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="setting-group-heading">
              <strong>Additional settings</strong>
              <small>Control availability, selection, quantity, and discounts.</small>
            </div>
            <div className="setting-list">
              <label className="setting-row">
                <span>
                  <strong>Exclude products already in cart</strong>
                  <small>Hide recommendations already in the customer cart.</small>
                </span>
                <input type="checkbox" checked={excludeInCart} onChange={(event) => setExcludeInCart(event.currentTarget.checked)} />
              </label>
              <label className="setting-row">
                <span>
                  <strong>Exclude unavailable variants</strong>
                  <small>Remove sold-out variants from the offer.</small>
                </span>
                <input type="checkbox" checked={excludeUnavailable} onChange={(event) => setExcludeUnavailable(event.currentTarget.checked)} />
              </label>
              {isProductOnly ? (
                <>
                  <label className="setting-row">
                    <span>
                      <strong>Preselect the complete bundle</strong>
                      <small>Customers can remove individual products and add only their selection.</small>
                    </span>
                    <input type="checkbox" checked={preselectAll} onChange={(event) => setPreselectAll(event.currentTarget.checked)} />
                  </label>
                  {isAfterAddProducts ? <label className="setting-row">
                    <span>
                      <strong>Match the added product quantity</strong>
                      <small>Use the triggering product quantity for every selected bundle product.</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={multiplyByTriggerQuantity}
                      onChange={(event) => setMultiplyByTriggerQuantity(event.currentTarget.checked)}
                    />
                  </label> : null}
                </>
              ) : null}
              <label className="setting-row">
                <span>
                  <strong>Discount recommended products</strong>
                  <small>Apply a timed discount to products added through this offer.</small>
                </span>
                <input type="checkbox" checked={discountEnabled} onChange={(event) => setDiscountEnabled(event.currentTarget.checked)} />
              </label>
            </div>
            {discountEnabled ? (
              <div className="form-grid discount-grid">
                <label>
                  Discount percentage
                  <input
                    min={1}
                    max={50}
                    step={1}
                    type="number"
                    value={discountPercentage}
                    onChange={(event) => setDiscountPercentage(Number(event.currentTarget.value))}
                  />
                </label>
                <label>
                  Countdown minutes
                  <input
                    min={1}
                    max={240}
                    step={1}
                    type="number"
                    value={discountCountdownMinutes}
                    onChange={(event) => setDiscountCountdownMinutes(Number(event.currentTarget.value))}
                  />
                </label>
              </div>
            ) : null}
          </s-section>

          <s-section heading={isProductPageAddons ? "Block copy and design" : "Modal copy and design"}>
            <div className="language-row">
              <label>
                Language
                <select value={activeLocale} onChange={(event) => setActiveLocale(event.currentTarget.value)}>
                  {locales.map((locale) => (
                    <option key={locale.locale} value={locale.locale}>
                      {locale.label} ({locale.locale})
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="modal-design-grid">
              <div>
                <div className="form-grid form-grid-wide">
                  <label>
                    {isProductPageAddons ? "Block title" : "Modal title"}
                    <input value={activeCopy.modalTitle} onChange={(event) => updateCopyField("modalTitle", event.currentTarget.value)} />
                  </label>
                  <label>
                    Subtitle
                    <input value={activeCopy.subtitle} onChange={(event) => updateCopyField("subtitle", event.currentTarget.value)} />
                  </label>
                  {isPreCheckout ? (
                    <label>
                      Services heading
                      <input value={activeCopy.servicesHeading} onChange={(event) => updateCopyField("servicesHeading", event.currentTarget.value)} />
                    </label>
                  ) : null}
                  {isPreCheckout ? (
                    <>
                      <label>
                        Products heading
                        <input value={activeCopy.productsHeading} onChange={(event) => updateCopyField("productsHeading", event.currentTarget.value)} />
                      </label>
                      <label>
                        Discount badge
                        <input value={activeCopy.discountBadgeLabel} onChange={(event) => updateCopyField("discountBadgeLabel", event.currentTarget.value)} />
                      </label>
                    </>
                  ) : null}
                  <label>
                    {isProductOnly ? "Checkbox action label" : "Product add button"}
                    <input value={activeCopy.productAddLabel} onChange={(event) => updateCopyField("productAddLabel", event.currentTarget.value)} />
                  </label>
                  {isPreCheckout ? (
                    <label>
                      Product added button
                      <input value={activeCopy.productAddedLabel} onChange={(event) => updateCopyField("productAddedLabel", event.currentTarget.value)} />
                    </label>
                  ) : null}
                  <label>
                    Variant picker label
                    <input value={activeCopy.productVariantLabel} onChange={(event) => updateCopyField("productVariantLabel", event.currentTarget.value)} />
                  </label>
                  {isPreCheckout ? (
                    <>
                      <label>
                        Previous products label
                        <input value={activeCopy.productsPreviousLabel} onChange={(event) => updateCopyField("productsPreviousLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Next products label
                        <input value={activeCopy.productsNextLabel} onChange={(event) => updateCopyField("productsNextLabel", event.currentTarget.value)} />
                      </label>
                    </>
                  ) : null}
                  {!isProductPageAddons ? <label>
                    Close label
                    <input value={activeCopy.closeLabel} onChange={(event) => updateCopyField("closeLabel", event.currentTarget.value)} />
                  </label> : null}
                  <label>
                    Added confirmation
                    <input value={activeCopy.addedToCartLabel} onChange={(event) => updateCopyField("addedToCartLabel", event.currentTarget.value)} />
                  </label>
                  {isAfterAddProducts ? (
                    <>
                      <label>
                        Expert badge
                        <input value={activeCopy.bundleExpertBadgeLabel} onChange={(event) => updateCopyField("bundleExpertBadgeLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Default item category
                        <input value={activeCopy.bundleItemLabel} onChange={(event) => updateCopyField("bundleItemLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Default item benefit
                        <input value={activeCopy.bundleItemDescription} onChange={(event) => updateCopyField("bundleItemDescription", event.currentTarget.value)} />
                      </label>
                      <label>
                        Complete price label
                        <input value={activeCopy.bundleCompletePriceLabel} onChange={(event) => updateCopyField("bundleCompletePriceLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Selected price label
                        <input value={activeCopy.bundleSelectedPriceLabel} onChange={(event) => updateCopyField("bundleSelectedPriceLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Complete bundle CTA
                        <input value={activeCopy.bundleCompleteLabel} onChange={(event) => updateCopyField("bundleCompleteLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Selected products CTA
                        <input value={activeCopy.bundleSelectedLabel} onChange={(event) => updateCopyField("bundleSelectedLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Bundle loading CTA
                        <input value={activeCopy.bundleAddingLabel} onChange={(event) => updateCopyField("bundleAddingLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Continue shopping CTA
                        <input value={activeCopy.continueShoppingLabel} onChange={(event) => updateCopyField("continueShoppingLabel", event.currentTarget.value)} />
                      </label>
                    </>
                  ) : isProductPageAddons ? (
                    <>
                      <label>
                        Eyebrow label
                        <input value={activeCopy.bundleExpertBadgeLabel} onChange={(event) => updateCopyField("bundleExpertBadgeLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Default item category
                        <input value={activeCopy.bundleItemLabel} onChange={(event) => updateCopyField("bundleItemLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Default item benefit
                        <input value={activeCopy.bundleItemDescription} onChange={(event) => updateCopyField("bundleItemDescription", event.currentTarget.value)} />
                      </label>
                      <label>
                        Add selected CTA
                        <input value={activeCopy.bundleSelectedLabel} onChange={(event) => updateCopyField("bundleSelectedLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Adding CTA
                        <input value={activeCopy.bundleAddingLabel} onChange={(event) => updateCopyField("bundleAddingLabel", event.currentTarget.value)} />
                      </label>
                    </>
                  ) : (
                    <>
                      <label>
                        Primary CTA
                        <input value={activeCopy.continueLabel} onChange={(event) => updateCopyField("continueLabel", event.currentTarget.value)} />
                      </label>
                      <label>
                        Secondary CTA
                        <input value={activeCopy.declineLabel} onChange={(event) => updateCopyField("declineLabel", event.currentTarget.value)} />
                      </label>
                    </>
                  )}
                  {!isProductPageAddons ? (
                    <>
                      <label>
                        Product add error
                        <input value={activeCopy.selectedProductError} onChange={(event) => updateCopyField("selectedProductError", event.currentTarget.value)} />
                      </label>
                      <label>
                        Items add error
                        <input value={activeCopy.selectedItemsError} onChange={(event) => updateCopyField("selectedItemsError", event.currentTarget.value)} />
                      </label>
                    </>
                  ) : null}
                  <label>
                    General add error
                    <input value={activeCopy.addFailedError} onChange={(event) => updateCopyField("addFailedError", event.currentTarget.value)} />
                  </label>
                  {isPreCheckout ? (
                    <label>
                      Layout
                      <select value={layout} onChange={(event) => setLayout(event.currentTarget.value)}>
                        <option value="services_first">Services first</option>
                        <option value="products_first">Products first</option>
                        <option value="two_column">Two-column desktop</option>
                      </select>
                    </label>
                  ) : null}
                </div>
                <div className="color-grid">
                  <label>
                    Accent
                    <input type="color" value={accentColor} onChange={(event) => setAccentColor(event.currentTarget.value)} />
                  </label>
                  <label>
                    Button text
                    <input type="color" value={buttonTextColor} onChange={(event) => setButtonTextColor(event.currentTarget.value)} />
                  </label>
                  <label>
                    {isProductPageAddons ? "Block background" : "Modal background"}
                    <input type="color" value={modalBackgroundColor} onChange={(event) => setModalBackgroundColor(event.currentTarget.value)} />
                  </label>
                  <label>
                    Text
                    <input type="color" value={textColor} onChange={(event) => setTextColor(event.currentTarget.value)} />
                  </label>
                </div>
              </div>
            </div>
          </s-section>
        </main>
        <aside className="campaign-preview-column" aria-label="Campaign preview">
          <div className="campaign-preview-heading">
            <span><strong>Live preview</strong><small>Updates as you edit</small></span>
            <span className="preview-badge">{selectedCampaignType?.presentationLabel}</span>
          </div>
          <LivePreview
            copy={activeCopy}
            services={isProductOnly
              ? []
              : services.map((service) => ({
                  id: service.id,
                  title: service.title,
                  description: descriptionValue(service, activeLocale),
                  badge: badgeValue(service, activeLocale),
                  imageUrl: service.variant?.imageUrl,
                  selectedByDefault: service.selectedByDefault,
                }))}
            products={recommendationMode !== CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS
              ? undefined
              : recommendedProducts.length
                ? recommendedProducts.map((product) => ({
                    id: product.id,
                    title: product.title,
                    imageUrl: product.imageUrl,
                    variantTitle: product.variantTitle,
                    bundleLabel: localizedProductValue(product.bundleLabel, activeLocale, activeCopy.bundleItemLabel),
                    bundleDescription: localizedProductValue(product.bundleDescription, activeLocale, activeCopy.bundleItemDescription),
                  }))
                : isProductOnly
                  ? undefined
                  : []}
            design={{
              accentColor,
              buttonTextColor,
              modalBackgroundColor,
              textColor,
              layout: isProductOnly ? "products_first" : layout,
              maxRecommendedProducts,
            }}
            discount={{ enabled: discountEnabled, percentage: discountPercentage, countdownMinutes: discountCountdownMinutes }}
            placement={trigger}
            bundle={{ preselectAll, multiplyByTriggerQuantity }}
          />
        </aside>
      </div>
    </s-page>
  );
}

function ResourceList({ resources, onRemove }: { resources: PickedResource[]; onRemove: (id: string) => void }) {
  if (!resources.length) return <p className="resource-empty">Nothing selected yet.</p>;
  return (
    <div className="targeting-resource-list">
      {resources.map((resource) => (
        <span key={resource.id}>
          <strong>{resource.title}</strong>
          <button aria-label={`Remove ${resource.title}`} type="button" onClick={() => onRemove(resource.id)}>×</button>
        </span>
      ))}
    </div>
  );
}
